/** theme.js — GGC global JavaScript. Deferred, runs after DOM is ready. */

/* ── Scroll-triggered animations ── */

(function initScrollAnimations() {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.12 });

  document.querySelectorAll('.animate-on-scroll').forEach(el => observer.observe(el));
})();

/* ── Cart utilities ── */

async function updateCartCount() {
  const res = await fetch('/cart.js');
  const cart = await res.json();
  document.querySelectorAll('.cart-count').forEach(el => {
    el.textContent = cart.item_count;
  });
}

async function addToCart(variantId, quantity = 1, sellingPlan = null) {
  const item = { id: variantId, quantity };
  if (sellingPlan) item.selling_plan = sellingPlan;

  const btn = document.querySelector('[data-atc]');
  if (btn) { btn.textContent = 'Adding...'; btn.disabled = true; }

  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [item] })
    });

    await updateCartCount();

    if (res.ok) {
      if (btn) {
        btn.textContent = 'Added';
        setTimeout(() => { btn.textContent = 'Add to Ritual'; btn.disabled = false; }, 2000);
      }
    } else {
      if (btn) { btn.textContent = 'Add to Ritual'; btn.disabled = false; }
    }
  } catch (err) {
    await updateCartCount();
    if (btn) { btn.textContent = 'Add to Ritual'; btn.disabled = false; }
  }
}

async function addBundle(variantIds) {
  const items = variantIds.map(id => ({ id, quantity: 1 }));
  try {
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    });
  } finally {
    await updateCartCount();
  }
}

/* ── Add-to-cart buttons (homepage + collection offering cards) ── */

(function initAddToCart() {
  document.querySelectorAll('button[data-product-id]:not([data-atc]):not([data-native-pre-order-btn])').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      addToCart(btn.dataset.productId);
    });
  });

  document.querySelectorAll('[data-bundle-ids]').forEach(btn => {
    btn.addEventListener('click', () => {
      const ids = btn.dataset.bundleIds.split(',').map(id => id.trim());
      addBundle(ids);
    });
  });
})();

/* ── Mobile nav drawer ── */

(function initNavDrawer() {
  const hamburger = document.querySelector('.nav-hamburger');
  const drawer    = document.querySelector('.nav-drawer');
  const overlay   = document.querySelector('.nav-overlay');

  if (!hamburger || !drawer) return;

  function openDrawer() {
    drawer.setAttribute('data-open', 'true');
    hamburger.setAttribute('aria-expanded', 'true');
  }
  function closeDrawer() {
    drawer.setAttribute('data-open', 'false');
    hamburger.setAttribute('aria-expanded', 'false');
  }

  hamburger.addEventListener('click', () => {
    const isOpen = drawer.getAttribute('data-open') === 'true';
    isOpen ? closeDrawer() : openDrawer();
  });

  if (overlay) overlay.addEventListener('click', closeDrawer);

  const closeBtn = document.querySelector('[data-nav-close]');
  if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
})();

/* ── Product page: variant selector + qty stepper ── */

(function initProductPage() {
  const purchaseRow = document.querySelector('.purchase-row');
  if (!purchaseRow) return;

  let selectedSellingPlan = null;

  // Variant pills — only present on multi-variant products
  const pills = document.querySelectorAll('.pill[data-variant-id]');
  pills.forEach(pill => {
    pill.addEventListener('click', () => {
      if (pill.classList.contains('sold-out')) return;
      pills.forEach(p => p.classList.remove('active'));
      pill.classList.add('active');
      // Keep data-starting-variant in sync so it survives PreProduct button replacement
      const atcBtn = document.querySelector('[data-atc]');
      if (atcBtn) atcBtn.dataset.startingVariant = pill.dataset.variantId;
    });
  });

  // Auto-select first available pill
  const firstAvailable = document.querySelector('.pill:not(.sold-out)');
  if (firstAvailable) firstAvailable.click();

  // Purchase option selector (pre-order billing)
  document.querySelectorAll('.purchase-option').forEach(option => {
    option.addEventListener('click', () => {
      document.querySelectorAll('.purchase-option').forEach(o => o.classList.remove('active'));
      option.classList.add('active');
      selectedSellingPlan = option.dataset.sellingPlan || null;
    });
  });

  const firstOption = document.querySelector('.purchase-option');
  if (firstOption) firstOption.click();

  // Qty stepper (product page only — cart uses .cart-qty-stepper)
  purchaseRow.querySelectorAll('.qty-stepper').forEach(stepper => {
    const display = stepper.querySelector('.qty-val');
    stepper.querySelector('.qty-minus')?.addEventListener('click', () => {
      const current = parseInt(display.textContent);
      if (current > 1) display.textContent = current - 1;
    });
    stepper.querySelector('.qty-plus')?.addEventListener('click', () => {
      display.textContent = parseInt(display.textContent) + 1;
    });
  });

  // ATC — delegate on .purchase-row so listener survives PreProduct replacing the button.
  // Skip if button has data-native-pre-order-btn (PreProduct owns those).
  // Use data-starting-variant as the variant ID — it is set from Liquid and kept in sync
  // by the pill handler above, so it survives button node replacement.
  let adding = false;
  purchaseRow.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-atc]');
    if (!btn || btn.disabled || adding || btn.hasAttribute('data-native-pre-order-btn')) return;
    adding = true;
    const variantId = btn.dataset.startingVariant;
    const qty = parseInt(purchaseRow.querySelector('.qty-val')?.textContent || 1);
    addToCart(variantId, qty, selectedSellingPlan).finally(() => {
      adding = false;
    });
  });
})();

// Refresh cart count after any app (e.g. PreProduct) completes a cart operation.
document.addEventListener('cart:updated', updateCartCount);
document.addEventListener('cart:update', updateCartCount);

/* ── Cart page: promo / discount code ── */

(function initPromoCode() {
  const input     = document.querySelector('.promo-input');
  const applyBtn  = document.querySelector('.promo-btn');
  const checkoutBtn = document.querySelector('.cart-checkout-btn');

  if (!input || !applyBtn) return;

  function checkoutUrlWithDiscount(code) {
    return '/checkout' + (code ? '?discount=' + encodeURIComponent(code) : '');
  }

  // Keep checkout button URL in sync as user types
  if (checkoutBtn) {
    input.addEventListener('input', function() {
      checkoutBtn.href = checkoutUrlWithDiscount(input.value.trim());
    });
  }

  // Apply button: navigate to checkout with discount code
  applyBtn.addEventListener('click', function() {
    var code = input.value.trim();
    if (!code) return;
    window.location.href = checkoutUrlWithDiscount(code);
  });
})();

/* ── Cart page: update qty + remove ── */

(function initCartPage() {
  async function updateCartItem(key, quantity) {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    location.reload();
  }

  document.querySelectorAll('[data-remove-key]').forEach(btn => {
    btn.addEventListener('click', () => updateCartItem(btn.dataset.removeKey, 0));
  });

  document.querySelectorAll('.cart-qty-stepper').forEach(stepper => {
    const key = stepper.dataset.lineKey;
    const display = stepper.querySelector('.qty-val');
    stepper.querySelector('.qty-minus')?.addEventListener('click', () => {
      const qty = Math.max(0, parseInt(display.textContent) - 1);
      display.textContent = qty;
      updateCartItem(key, qty);
    });
    stepper.querySelector('.qty-plus')?.addEventListener('click', () => {
      const qty = parseInt(display.textContent) + 1;
      display.textContent = qty;
      updateCartItem(key, qty);
    });
  });
})();
